.container {
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  padding: var(--aura-space-4);
  border-radius: var(--aura-radius-2xl);
  background: color-mix(
    in srgb,
    var(--aura-color-glass-surface) 92%,
    transparent
  );
  border: 1px solid
    color-mix(in srgb, var(--aura-color-global-border-soft) 80%, transparent);
  color: var(--aura-color-global-text-primary);
  backdrop-filter: var(
    --glass-data-chart-backdrop-filter,
    blur(var(--aura-glass-neutral-level2-backdrop-blur))
  );
  -webkit-backdrop-filter: var(
    --glass-data-chart-backdrop-filter,
    blur(var(--aura-glass-neutral-level2-backdrop-blur))
  );
  box-shadow: var(
    --glass-elev-chart-data-container,
    0 18px 48px rgba(var(--glass-color-black) / var(--glass-opacity-25))
  );
  overflow: hidden;
  isolation: isolate;
  transition:
    transform var(--aura-motion-duration-medium)
      var(--aura-motion-easing-standard),
    box-shadow var(--aura-motion-duration-medium)
      var(--aura-motion-easing-standard);
}

.header {
  display: flex;
  flex-direction: column;
  gap: var(--aura-space-2);
  margin-bottom: var(--aura-space-4);
}

.title {
  margin: 0;
  font-size: var(--aura-font-size-xl);
  font-weight: var(--aura-font-weight-semibold);
  color: var(--aura-color-global-text-primary);
  overflow-wrap: anywhere;
}

.subtitle {
  margin: 0;
  font-size: var(--aura-font-size-sm);
  color: color-mix(
    in srgb,
    var(--aura-color-global-text-secondary) 80%,
    transparent
  );
  overflow-wrap: anywhere;
}

.wrapper {
  position: relative;
  flex: 1;
  width: 100%;
  min-height: 0;
  min-width: 0;
}

.legend {
  display: flex;
  flex-wrap: wrap;
  gap: var(--aura-space-3);
  padding: var(--aura-space-3);
  margin-top: var(--aura-space-4);
  min-width: 0;
}

.legendTop {
  margin-bottom: var(--aura-space-4);
  margin-top: 0;
}

.legendBottom {
  margin-top: var(--aura-space-4);
}

.legendLeft,
.legendRight {
  flex-direction: column;
  align-items: flex-start;
}

.legendGlass {
  backdrop-filter: blur(var(--aura-glass-neutral-level2-backdrop-blur));
  -webkit-backdrop-filter: blur(var(--aura-glass-neutral-level2-backdrop-blur));
  background: color-mix(
    in srgb,
    var(--aura-color-glass-overlay) 65%,
    transparent
  );
  border-radius: var(--aura-radius-xl);
}

.legendItem {
  display: flex;
  align-items: center;
  gap: var(--aura-space-2);
  min-width: 0;
  cursor: pointer;
  transition: opacity var(--aura-motion-duration-fast)
    var(--aura-motion-easing-standard);
}

.legendItemInactive {
  opacity: 0.5;
}

.legendItem:hover {
  opacity: 0.8;
}

.legendColor {
  width: 12px;
  height: 12px;
  border-radius: var(--aura-radius-sm);
  box-shadow: var(
    --glass-elev-chart-legend-color,
    0 6px 16px rgba(15, 23, 42, 0.16)
  );
}

.legendLabel {
  font-size: var(--aura-font-size-sm);
  font-weight: var(--aura-font-weight-medium);
  color: color-mix(
    in srgb,
    var(--aura-color-global-text-secondary) 85%,
    transparent
  );
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.tooltip {
  position: absolute;
  z-index: 1000;
  pointer-events: none;
  font-family: var(--aura-font-family-sans);
  font-size: var(--aura-font-size-xs);
  border-radius: var(--aura-radius-lg);
  box-shadow: var(
    --glass-elev-chart-tooltip,
    0 20px 48px rgba(15, 23, 42, 0.32)
  );
  backdrop-filter: blur(var(--aura-glass-neutral-level3-backdrop-blur));
  -webkit-backdrop-filter: blur(var(--aura-glass-neutral-level3-backdrop-blur));
  background: color-mix(
    in srgb,
    var(--aura-color-glass-overlay) 90%,
    var(--glass-chart-tooltip-base, rgba(8, 16, 32, 0.72))
  );
  border: 1px solid
    color-mix(in srgb, var(--aura-color-global-border-soft) 70%, transparent);
  color: var(--aura-color-global-text-inverse);
  padding: var(--aura-space-3) var(--aura-space-4);
  min-width: 120px;
  max-width: 280px;
  white-space: normal;
  overflow: hidden;
  overflow-wrap: anywhere;
  transition:
    transform var(--aura-motion-duration-fast)
      var(--aura-motion-easing-standard),
    opacity var(--aura-motion-duration-fast) var(--aura-motion-easing-standard);
}

.tooltipHeader {
  font-size: var(--aura-font-size-sm);
  font-weight: var(--aura-font-weight-semibold);
  margin-bottom: var(--aura-space-2);
  padding-bottom: var(--aura-space-2);
  border-bottom: 1px solid
    color-mix(in srgb, var(--aura-color-global-border-soft) 65%, transparent);
}

.tooltipRow {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: var(--aura-space-2);
  margin-top: var(--aura-space-1);
  min-width: 0;
}

.tooltipLabel {
  font-size: var(--aura-font-size-xs);
  color: color-mix(
    in srgb,
    var(--aura-color-global-text-secondary) 85%,
    transparent
  );
  min-width: 0;
  overflow-wrap: anywhere;
}

.tooltipValue {
  font-size: var(--aura-font-size-sm);
  font-weight: var(--aura-font-weight-semibold);
  color: var(--aura-color-global-text-inverse);
  overflow-wrap: anywhere;
}

.emptyState {
  display: flex;
  min-height: 220px;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: var(--aura-space-2);
  padding: var(--aura-space-6);
  text-align: center;
  border-radius: var(--aura-radius-xl);
  background: color-mix(
    in srgb,
    var(--aura-color-glass-overlay) 72%,
    rgba(var(--glass-color-black) / var(--glass-opacity-25))
  );
  border: 1px solid
    color-mix(in srgb, var(--aura-color-global-border-soft) 68%, transparent);
}

.emptyStateTitle {
  font-size: var(--aura-font-size-base);
  font-weight: var(--aura-font-weight-semibold);
  color: var(--aura-color-global-text-primary);
}

.emptyStateDescription {
  max-width: 34rem;
  margin: 0;
  font-size: var(--aura-font-size-sm);
  color: color-mix(
    in srgb,
    var(--aura-color-global-text-secondary) 88%,
    transparent
  );
  overflow-wrap: anywhere;
}

@media (max-width: 640px) {
  .container {
    padding: var(--aura-space-4);
  }

  .legend {
    gap: var(--aura-space-2);
    padding-inline: 0;
  }

  .tooltip {
    max-width: min(260px, calc(100vw - 32px));
  }
}

@media (prefers-reduced-motion: reduce) {
  .container,
  .legendItem,
  .tooltip {
    transition: none;
  }
}
